import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, TrendingUp, Activity, AlertTriangle, Circle, Plus } from 'lucide-react';
import api from '../../lib/api';

interface VentureTask {
  id: string;
  title: string;
  priority: string | null;
  due_date: string | null;
}

interface VentureItem {
  id: string;
  name: string;
  stage: string | null;
  open_tasks: number;
  blockers: string | null;
  next_tasks?: VentureTask[];
  status?: string | null;
  is_stalled?: boolean;
  is_going_cold?: boolean;
  days_since_activity?: number;
  last_activity_at?: string;
}

interface VenturesCardProps {
  data?: VentureItem[] | null;
  onTaskCompleted?: () => void;
  onMutated?: () => void;
}

const STAGE_STYLE: Record<string, { bg: string; text: string; border: string }> = {
  Idea:     { bg: 'rgba(20,20,40,0.04)', text: 'var(--muted)',   border: 'var(--border)' },
  Building: { bg: 'rgba(110,168,255,0.10)', text: 'var(--sky)',  border: 'rgba(110,168,255,0.25)' },
  Revenue:  { bg: 'rgba(31,157,110,0.10)',  text: 'var(--success)', border: 'rgba(31,157,110,0.25)' },
  Scaling:  { bg: 'var(--gold-dim)',         text: 'var(--gold)', border: 'rgba(232,155,48,0.25)' },
  Exited:   { bg: 'rgba(20,20,40,0.04)',    text: 'var(--muted)', border: 'var(--border)' },
};

function StatusIcon({ venture }: { venture: VentureItem }) {
  if (venture.is_stalled) return <AlertTriangle size={14} style={{ color: 'var(--danger)', flexShrink: 0 }} />;
  if (venture.is_going_cold) return <Activity size={14} style={{ color: 'var(--gold)', flexShrink: 0 }} />;
  if (venture.stage === 'Revenue' || venture.stage === 'Scaling') return <TrendingUp size={14} style={{ color: 'var(--success)', flexShrink: 0 }} />;
  return <Circle size={8} fill="var(--gold)" stroke="none" style={{ flexShrink: 0 }} />;
}

export default function VenturesCard({ data, onTaskCompleted, onMutated }: VenturesCardProps) {
  const navigate = useNavigate();
  const [completing, setCompleting] = useState<Record<string, boolean>>({});
  const [completed, setCompleted] = useState<Record<string, boolean>>({});
  const [errored, setErrored] = useState<Record<string, boolean>>({});
  const [addingFor, setAddingFor] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const addInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (addingFor) addInputRef.current?.focus();
  }, [addingFor]);

  const notifyMutated = () => {
    (onMutated ?? onTaskCompleted)?.();
  };

  const openAdd = (ventureId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setAddError(null);
    setNewTitle('');
    setAddingFor(ventureId);
  };

  const cancelAdd = () => {
    setAddingFor(null);
    setNewTitle('');
    setAddError(null);
  };

  const submitAdd = async (ventureId: string) => {
    const title = newTitle.trim();
    if (!title) {
      cancelAdd();
      return;
    }
    setSubmitting(true);
    setAddError(null);
    try {
      await api.post(`/ventures/${ventureId}/tasks`, { title });
      setAddingFor(null);
      setNewTitle('');
      notifyMutated();
    } catch {
      setAddError("Couldn't add task — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!data || data.length === 0) return null;

  const goToVentures = () => navigate('/ventures');

  const handleDone = async (taskId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (completing[taskId] || completed[taskId]) return;
    setCompleting((s) => ({ ...s, [taskId]: true }));
    setErrored((s) => ({ ...s, [taskId]: false }));
    try {
      await api.put('/ventures/tasks/' + taskId + '/complete');
      setCompleted((s) => ({ ...s, [taskId]: true }));
      setCompleting((s) => ({ ...s, [taskId]: false }));
      notifyMutated();
    } catch {
      setCompleting((s) => ({ ...s, [taskId]: false }));
      setErrored((s) => ({ ...s, [taskId]: true }));
    }
  };

  return (
    <div style={{ marginTop: 24 }}>
      <div className="lumen-glass lumen-spring" style={{ borderRadius: 20, padding: 8 }}>
        {data.map((v, i) => {
          const tasks = (v.next_tasks || []).filter((t) => !completed[t.id]);
          const days = v.days_since_activity;
          const stage = STAGE_STYLE[v.stage || ''] || STAGE_STYLE.Idea;

          return (
            <div key={v.id}>
              <div
                onClick={goToVentures}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 14px',
                  cursor: 'pointer',
                  borderRadius: 12,
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.5)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                  <StatusIcon venture={v} />
                  <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {v.name}
                  </span>
                  <span style={{ color: 'var(--subtle)', fontSize: 13 }}>·</span>
                  <span style={{ fontSize: 13, color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                    {v.is_stalled ? (
                      <span style={{ color: 'var(--danger)' }}>Stalled{typeof days === 'number' && days > 0 ? ` · ${days}d` : ''}</span>
                    ) : v.is_going_cold ? (
                      <span style={{ color: 'var(--gold)' }}>Going cold{typeof days === 'number' ? ` · ${days}d` : ''}</span>
                    ) : (
                      `${v.open_tasks} ${v.open_tasks === 1 ? 'task' : 'tasks'}`
                    )}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  {v.stage && (
                    <span style={{
                      fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 6,
                      background: stage.bg, color: stage.text, border: `1px solid ${stage.border}`,
                    }}>
                      {v.stage}
                    </span>
                  )}
                  <button
                    onClick={(e) => openAdd(v.id, e)}
                    aria-label={`Add task to ${v.name}`}
                    title="Add task"
                    style={{
                      width: 24, height: 24, borderRadius: '50%',
                      background: 'rgba(20,20,40,0.04)', border: '1px solid var(--border)',
                      color: 'var(--muted)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', padding: 0, flexShrink: 0,
                    }}
                  >
                    <Plus size={13} />
                  </button>
                  <ChevronRight size={16} style={{ color: 'var(--subtle)' }} />
                </div>
              </div>

              {v.blockers && (
                <div style={{ padding: '0 14px 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <AlertTriangle size={12} style={{ color: 'var(--danger)', flexShrink: 0 }} />
                  <p style={{ color: 'var(--danger)', fontSize: 12, fontStyle: 'italic', margin: 0 }}>{v.blockers}</p>
                </div>
              )}

              {addingFor === v.id && (
                <div style={{ padding: '0 14px 10px' }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    background: 'rgba(255,255,255,0.6)', border: '1px solid var(--border)',
                    borderRadius: 10, padding: '6px 6px 6px 10px',
                  }}>
                    <input
                      ref={addInputRef}
                      type="text"
                      value={newTitle}
                      onChange={(e) => setNewTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); submitAdd(v.id); }
                        else if (e.key === 'Escape') { e.preventDefault(); cancelAdd(); }
                      }}
                      onBlur={() => {
                        if (!submitting && !newTitle.trim()) cancelAdd();
                      }}
                      placeholder="New task…"
                      aria-label={`New task for ${v.name}`}
                      disabled={submitting}
                      style={{
                        flex: 1, minWidth: 0, background: 'transparent', border: 'none',
                        outline: 'none', color: 'var(--text)', fontSize: 13,
                        fontFamily: 'inherit', padding: '4px 0',
                      }}
                    />
                    <button
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => submitAdd(v.id)}
                      disabled={submitting || !newTitle.trim()}
                      style={{
                        fontSize: 11, fontWeight: 600, padding: '5px 12px', borderRadius: 8,
                        background: newTitle.trim() ? 'var(--gold)' : 'rgba(20,20,40,0.04)',
                        color: newTitle.trim() ? '#fff' : 'var(--subtle)', border: 'none',
                        cursor: submitting ? 'wait' : (newTitle.trim() ? 'pointer' : 'default'),
                        fontFamily: 'inherit', flexShrink: 0,
                        opacity: submitting ? 0.6 : 1,
                      }}
                    >
                      {submitting ? '...' : 'Add'}
                    </button>
                  </div>
                  {addError && (
                    <p style={{ color: 'var(--danger)', fontSize: 11, marginTop: 4, marginLeft: 4 }}>
                      {addError}
                    </p>
                  )}
                </div>
              )}

              {tasks.length > 0 && (
                <div style={{ padding: '0 14px 10px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {tasks.map((t) => (
                    <div key={t.id}>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        background: 'rgba(255,255,255,0.6)', border: '1px solid var(--border)',
                        borderRadius: 10, padding: '8px 10px',
                      }}>
                        <span style={{ fontSize: 13, color: 'var(--text)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</span>
                        <button
                          onClick={(e) => handleDone(t.id, e)}
                          disabled={!!completing[t.id]}
                          style={{
                            fontSize: 11, fontWeight: 600, padding: '5px 12px', borderRadius: 8,
                            background: 'linear-gradient(135deg, var(--gold-light), var(--gold))', color: '#fff', border: 'none',
                            cursor: completing[t.id] ? 'wait' : 'pointer', fontFamily: 'inherit',
                            opacity: completing[t.id] ? 0.6 : 1, flexShrink: 0,
                          }}
                        >
                          {completing[t.id] ? '...' : 'Done'}
                        </button>
                      </div>
                      {errored[t.id] && (
                        <p style={{ color: 'var(--danger)', fontSize: 11, marginTop: 4, marginLeft: 4 }}>
                          Couldn't mark done — tap again to retry.
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {i < data.length - 1 && (
                <div style={{ height: 1, background: 'var(--border)', margin: '0 14px' }} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
